import { BidType, MarketProgram } from '@/features/bidding/constants'

export const ENERGY_BIDDING_WRITE_ENABLED: MarketProgram[] = [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN]
export const ENERGY_BIDDING_READ_ENABLED: MarketProgram[] = [
  MarketProgram.MFRR_UP,
  MarketProgram.MFRR_DOWN,
  MarketProgram.AFRR_UP,
  MarketProgram.AFRR_DOWN,
]

export type CountryConfig = {
  daysInAdvance: number
  bidTypeConfig: Record<BidType, BidTypeConfig>
  marketsThatCanBeManuallyEdited: MarketProgram[]
  marketsThatRequirePositiveIntegerVolume: MarketProgram[]
  tsvImportEnabled: boolean
}

export type BidTypeConfig = {
  ptuLengthInMin: number
  volumeUnit: 'WATTS' | 'WATT_HOURS'
  priceLadderType: PriceLadderType
}

/**
 * Cumulative means the total volume equals the volume of the chunk with the highest quantity
 * Incremental means the total volume is the sum of all chunks
 * e.g. 1MW at 10€, 2MW at 20€, 3MW at 30€. Total volume would be 3MW in Cumulative but 6MW in Incremental.
 */
export enum PriceLadderType {
  'CUMULATIVE',
  'INCREMENTAL',
}

const BID_CONFIG: Partial<Record<CountryIdentifier, CountryConfig>> = {
  gr: {
    daysInAdvance: 1,
    bidTypeConfig: {
      [BidType.CAPACITY]: { ptuLengthInMin: 30, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.CUMULATIVE },
      [BidType.ENERGY]: { ptuLengthInMin: 15, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.CUMULATIVE },
    },
    marketsThatCanBeManuallyEdited: [],
    marketsThatRequirePositiveIntegerVolume: [],
    tsvImportEnabled: false,
  },
  fi: {
    daysInAdvance: 1,
    bidTypeConfig: {
      [BidType.CAPACITY]: { ptuLengthInMin: 60, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.INCREMENTAL },
      [BidType.ENERGY]: { ptuLengthInMin: 15, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.INCREMENTAL },
    },
    marketsThatCanBeManuallyEdited: [],
    marketsThatRequirePositiveIntegerVolume: [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN],
    tsvImportEnabled: true,
  },
  no: {
    daysInAdvance: 2,
    bidTypeConfig: {
      [BidType.CAPACITY]: { ptuLengthInMin: 60, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.INCREMENTAL },
      [BidType.ENERGY]: { ptuLengthInMin: 15, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.INCREMENTAL },
    },
    marketsThatCanBeManuallyEdited: [],
    marketsThatRequirePositiveIntegerVolume: [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN],
    tsvImportEnabled: true,
  },
  se: {
    daysInAdvance: 2,
    bidTypeConfig: {
      [BidType.CAPACITY]: { ptuLengthInMin: 60, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.INCREMENTAL },
      [BidType.ENERGY]: { ptuLengthInMin: 15, volumeUnit: 'WATTS', priceLadderType: PriceLadderType.INCREMENTAL },
    },
    marketsThatCanBeManuallyEdited: [
      MarketProgram.NORDICS_FCRD_DOWN_STATIC,
      MarketProgram.NORDICS_FCRD_UP_STATIC,
      MarketProgram.NORDICS_FCRD_DOWN_DYNAMIC,
      MarketProgram.NORDICS_FCRD_UP_DYNAMIC,
      MarketProgram.FCRN,
      MarketProgram.NORDICS_FCRN,
      MarketProgram.FFR_UP,
    ],
    marketsThatRequirePositiveIntegerVolume: [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN],
    tsvImportEnabled: true,
  },
}

const DEFAULT_CONFIG: BidTypeConfig = {
  ptuLengthInMin: 60,
  volumeUnit: 'WATTS',
  priceLadderType: PriceLadderType.INCREMENTAL,
}

const getBidConfig = (selectedCountry: CountryIdentifier, bidType: BidType): BidTypeConfig => {
  return BID_CONFIG[selectedCountry]?.bidTypeConfig[bidType] ?? DEFAULT_CONFIG
}

export const getByCountry = (selectedCountry: CountryIdentifier): Omit<CountryConfig, 'bidTypeConfig'> => {
  return (
    BID_CONFIG[selectedCountry] ?? {
      daysInAdvance: 2,
      marketsThatCanBeManuallyEdited: [],
      marketsThatRequirePositiveIntegerVolume: [],
      tsvImportEnabled: false,
    }
  )
}

export default getBidConfig
